import { Color, Mesh, Program, Renderer, Triangle } from 'ogl';
import React, { PropsWithChildren, useLayoutEffect, useRef } from 'react';

import usePersistent from 'hooks/use-persistent';

import fragment from './shaders/fragment.glsl?raw';
import vertex from './shaders/vertex.glsl?raw';
import useStyles from './styles';

interface Props {
    colors?: string[];
    amplitude?: number;
    blend?: number;
    speed?: number;
    className?: string;
}

const DEFAULT_COLOR_STOPS = ['#EF552A', '#FF8F57', '#BE4425'];

const Aurora: React.FC<PropsWithChildren<Props>> = ({
    colors = DEFAULT_COLOR_STOPS,
    amplitude = 1,
    blend = 0.5,
    speed = 1,
    className
}) => {
    const { classes, cx } = useStyles();
    const persistent = usePersistent({ colors, amplitude, blend, speed });
    const ctnDom = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const ctn = ctnDom.current;
        if (!ctn) return;

        const renderer = new Renderer({ alpha: true, premultipliedAlpha: true, antialias: true });
        const gl = renderer.gl;

        gl.clearColor(0, 0, 0, 0);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.canvas.style.backgroundColor = 'transparent';

        const program = new Program(gl, {
            vertex,
            fragment,
            uniforms: {
                uTime: { value: 0 },
                uAmplitude: { value: amplitude },
                uColorStops: {
                    value: colors.map((hex) => {
                        const { r, g, b } = new Color(hex);
                        return [r, g, b];
                    })
                },
                uResolution: { value: [ctn.offsetWidth, ctn.offsetHeight] },
                uBlend: { value: blend }
            }
        });

        function resize() {
            if (!ctn) return;
            const width = ctn.offsetWidth;
            const height = ctn.offsetHeight;
            renderer.setSize(width, height);
            if (program) {
                program.uniforms.uResolution.value = [width, height];
            }
        }
        window.addEventListener('resize', resize);

        const geometry = new Triangle(gl);
        if (geometry.attributes.uv) {
            delete geometry.attributes.uv;
        }

        const mesh = new Mesh(gl, { geometry, program });
        ctn.appendChild(gl.canvas);

        let animateId = 0;
        const update = (t: number) => {
            animateId = requestAnimationFrame(update);
            const time = t * 0.01;

            if (program) {
                program.uniforms.uTime.value = time * persistent.current.speed * 0.1;
                program.uniforms.uAmplitude.value = persistent.current.amplitude;
                program.uniforms.uBlend.value = persistent.current.blend;
                program.uniforms.uColorStops.value = persistent.current.colors.map((hex) => {
                    const { r, g, b } = new Color(hex);
                    return [r, g, b];
                });
                renderer.render({ scene: mesh });
            }
        };
        animateId = requestAnimationFrame(update);

        resize();

        return () => {
            cancelAnimationFrame(animateId);
            window.removeEventListener('resize', resize);
            if (ctn && gl.canvas.parentNode === ctn) {
                ctn.removeChild(gl.canvas);
            }
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        };
    }, [amplitude, blend, colors, persistent]);

    return <div ref={ctnDom} className={cx(classes.root, className)} />;
};

export default Aurora;
